import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  VStack,
  Text,
  Switch,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Divider,
  HStack,
  Tooltip,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Link,
  useToast,
} from '@chakra-ui/react';
import {
  FaUniversalAccess,
  FaAdjust,
  FaLink,
  FaTextHeight,
  FaArrowsAltH,
  FaPause,
  FaFont,
  FaMouse,
  FaComment,
  FaAlignLeft,
  FaTint,
  FaImage,
  FaGlobe,
  FaFileAlt,
} from 'react-icons/fa';
import accessibilityStats from './AccessibilityStats';
import LanguageSelector from './LanguageSelector';

const AccessibilityWidget = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  
  // Accessibility states
  const [contrast, setContrast] = useState(false);
  const [highlightLinks, setHighlightLinks] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const [textSpacing, setTextSpacing] = useState(100);
  const [pauseAnimations, setPauseAnimations] = useState(false);
  const [legibleFonts, setLegibleFonts] = useState(false);
  const [bigCursor, setBigCursor] = useState(false);
  const [lineHeight, setLineHeight] = useState(100);
  const [textAlign, setTextAlign] = useState(false);
  const [saturation, setSaturation] = useState(100);
  const [hideImages, setHideImages] = useState(false);

  // Apply accessibility settings
  useEffect(() => {
    try {
      const root = document.documentElement;
      
      // High Contrast
      if (contrast) {
        document.body.classList.add('high-contrast');
      } else {
        document.body.classList.remove('high-contrast');
      }

      // Highlight Links
      if (highlightLinks) {
        document.body.classList.add('highlight-links');
      } else {
        document.body.classList.remove('highlight-links');
      }

      // Font Size
      root.style.fontSize = `${fontSize}%`;

      // Text Spacing
      if (textSpacing !== 100) {
        const spacingValue = (textSpacing / 100);
        root.style.letterSpacing = `${(spacingValue - 1) * 2}px`;
        root.style.wordSpacing = `${(spacingValue - 1) * 4}px`;
      } else {
        root.style.letterSpacing = 'normal';
        root.style.wordSpacing = 'normal';
      }

      // Pause Animations
      if (pauseAnimations) {
        document.body.classList.add('pause-animations');
      } else {
        document.body.classList.remove('pause-animations');
      }

      // Legible Fonts (Dyslexia-friendly)
      if (legibleFonts) {
        root.style.fontFamily = 'Arial, Verdana, sans-serif';
      } else {
        root.style.fontFamily = '';
      }

      // Big Cursor
      if (bigCursor) {
        document.body.classList.add('big-cursor');
      } else {
        document.body.classList.remove('big-cursor');
      }

      // Line Height
      if (lineHeight !== 100) {
        root.style.lineHeight = `${lineHeight / 100 * 1.5}`;
      } else {
        root.style.lineHeight = '';
      }

      // Text Align (Left align everything)
      if (textAlign) {
        document.body.classList.add('text-align-left');
      } else {
        document.body.classList.remove('text-align-left');
      }

      // Saturation
      if (saturation !== 100) {
        document.body.style.filter = `saturate(${saturation}%)`;
      } else {
        document.body.style.filter = '';
      }

      // Hide Images
      if (hideImages) {
        document.body.classList.add('hide-images');
      } else {
        document.body.classList.remove('hide-images');
      }

      // Save settings to localStorage
      const settings = {
        contrast,
        highlightLinks,
        fontSize,
        textSpacing,
        pauseAnimations,
        legibleFonts,
        bigCursor,
        lineHeight,
        textAlign,
        saturation,
        hideImages,
      };
      localStorage.setItem('accessibilitySettings', JSON.stringify(settings));

    } catch (error) {
      console.error('Error applying accessibility settings:', error);
    }
  }, [contrast, highlightLinks, fontSize, textSpacing, pauseAnimations, 
      legibleFonts, bigCursor, lineHeight, textAlign, saturation, hideImages]);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem('accessibilitySettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setContrast(settings.contrast || false);
        setHighlightLinks(settings.highlightLinks || false);
        setFontSize(settings.fontSize || 100);
        setTextSpacing(settings.textSpacing || 100);
        setPauseAnimations(settings.pauseAnimations || false);
        setLegibleFonts(settings.legibleFonts || false);
        setBigCursor(settings.bigCursor || false);
        setLineHeight(settings.lineHeight || 100);
        setTextAlign(settings.textAlign || false);
        setSaturation(settings.saturation || 100);
        setHideImages(settings.hideImages || false);
      }
    } catch (error) {
      console.error('Error loading accessibility settings:', error);
    }
  }, []);

  const resetSettings = () => {
    try {
      setContrast(false);
      setHighlightLinks(false);
      setFontSize(100);
      setTextSpacing(100);
      setPauseAnimations(false);
      setLegibleFonts(false);
      setBigCursor(false);
      setLineHeight(100);
      setTextAlign(false);
      setSaturation(100);
      setHideImages(false);
      localStorage.removeItem('accessibilitySettings');
      
      toast({
        title: 'Settings Reset',
        description: 'All accessibility settings have been reset to defaults.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error resetting settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to reset settings. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Track feature usage
  const trackFeature = (featureName) => {
    try {
      accessibilityStats.trackFeatureUse(featureName);
    } catch (error) {
      // Silently fail - don't break functionality
      console.error('Error tracking feature:', error);
    }
  };

  // Start session tracking when widget opens
  useEffect(() => {
    try {
      if (isOpen) {
        accessibilityStats.startSession();
      } else {
        accessibilityStats.endSession();
      }
    } catch (error) {
      // Silently fail
      console.error('Error with session tracking:', error);
    }
  }, [isOpen]);

  const AccessibilityOption = ({ icon: Icon, label, isSwitch, value, onChange, isSlider, min = 50, max = 150 }) => (
    <HStack justify="space-between" w="100%" py={2}>
      <HStack spacing={3}>
        <Box color="brand.500">
          <Icon size={20} />
        </Box>
        <Text fontSize="sm" fontWeight="500">{label}</Text>
      </HStack>
      {isSwitch && (
        <Switch
          colorScheme="brand"
          isChecked={value}
          onChange={(e) => onChange(e.target.checked)}
        />
      )}
      {isSlider && (
        <Box w="120px">
          <Slider
            value={value}
            min={min}
            max={max}
            step={10}
            onChange={onChange}
            colorScheme="brand"
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb boxSize={4} />
          </Slider>
          <Text fontSize="xs" textAlign="center" mt={1} color="gray.500">
            {value}%
          </Text>
        </Box>
      )}
    </HStack>
  );

  return (
    <>
      {/* Floating Accessibility Button - MOVED TO LEFT */}
      <Tooltip label="Accessibility Options" placement="right" hasArrow>
        <IconButton
          icon={<FaUniversalAccess size={24} />}
          onClick={onOpen}
          position="fixed"
          bottom="20px"
          left="20px"
          size="lg"
          colorScheme="brand"
          borderRadius="full"
          boxShadow="xl"
          zIndex={9999}
          aria-label="Open Accessibility Menu"
          _hover={{
            transform: 'scale(1.1)',
            boxShadow: '2xl',
          }}
          transition="all 0.2s"
        />
      </Tooltip>

      {/* Accessibility Drawer - OPENS FROM LEFT */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" color="brand.700">
            <HStack spacing={2}>
              <FaUniversalAccess />
              <Text>Accessibility Options</Text>
            </HStack>
          </DrawerHeader>

          <DrawerBody py={6}>
            <Tabs colorScheme="brand" variant="enclosed">
              <TabList>
                <Tab fontSize="sm">
                  <FaUniversalAccess />
                  <Text ml={2}>Features</Text>
                </Tab>
                <Tab fontSize="sm">
                  <FaGlobe />
                  <Text ml={2}>Language</Text>
                </Tab>
              </TabList>

              <TabPanels>
                {/* Features Tab */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
              
              <AccessibilityOption
                icon={FaAdjust}
                label="High Contrast"
                isSwitch
                value={contrast}
                onChange={setContrast}
              />

              <Divider />

              <AccessibilityOption
                icon={FaLink}
                label="Highlight Links"
                isSwitch
                value={highlightLinks}
                onChange={setHighlightLinks}
              />

              <Divider />

              <AccessibilityOption
                icon={FaTextHeight}
                label="Bigger Text"
                isSlider
                value={fontSize}
                onChange={setFontSize}
              />

              <Divider />

              <AccessibilityOption
                icon={FaArrowsAltH}
                label="Text Spacing"
                isSlider
                value={textSpacing}
                onChange={setTextSpacing}
              />

              <Divider />

              <AccessibilityOption
                icon={FaPause}
                label="Pause Animations"
                isSwitch
                value={pauseAnimations}
                onChange={setPauseAnimations}
              />

              <Divider />

              <AccessibilityOption
                icon={FaFont}
                label="Dyslexia/Legible Fonts"
                isSwitch
                value={legibleFonts}
                onChange={setLegibleFonts}
              />

              <Divider />

              <AccessibilityOption
                icon={FaMouse}
                label="Big Cursor"
                isSwitch
                value={bigCursor}
                onChange={setBigCursor}
              />

              <Divider />

              <AccessibilityOption
                icon={FaComment}
                label="Reading Guide (Line Height)"
                isSlider
                value={lineHeight}
                onChange={setLineHeight}
              />

              <Divider />

              <AccessibilityOption
                icon={FaAlignLeft}
                label="Text Align Left"
                isSwitch
                value={textAlign}
                onChange={setTextAlign}
              />

              <Divider />

              <AccessibilityOption
                icon={FaTint}
                label="Color Saturation"
                isSlider
                value={saturation}
                onChange={setSaturation}
                min={0}
                max={150}
              />

              <Divider />

              <AccessibilityOption
                icon={FaImage}
                label="Hide Images"
                isSwitch
                value={hideImages}
                onChange={setHideImages}
              />

              <Divider />

              {/* Reset Button */}
              <Box pt={4}>
                <Button
                  w="100%"
                  colorScheme="red"
                  variant="outline"
                  onClick={resetSettings}
                  size="md"
                  leftIcon={<Text fontSize="lg">🔄</Text>}
                >
                  Reset All Settings
                </Button>
              </Box>

              {/* Info */}
              <Box bg="blue.50" p={4} borderRadius="md" mt={4}>
                <Text fontSize="xs" color="gray.600">
                  Your accessibility preferences are saved automatically and will persist across visits.
                </Text>
              </Box>

              {/* Accessibility Statement Link */}
              <Box textAlign="center" pt={2}>
                <Link href="/legal/accessibility-statement" color="brand.600" fontSize="sm" fontWeight="600">
                  <FaFileAlt style={{ display: 'inline', marginRight: '8px' }} />
                  View Accessibility Statement
                </Link>
              </Box>

            </VStack>
          </TabPanel>

          {/* Language Tab */}
          <TabPanel px={0}>
            <LanguageSelector />
          </TabPanel>

        </TabPanels>
      </Tabs>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default AccessibilityWidget;
